/* eslint-disable */
import React, { useEffect, useState } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { MenuItem, Paper, Select } from '@mui/material'
import moment from 'moment'
import Skeleton from 'react-loading-skeleton'
import { useFilter } from '../../../app/contexts/FilterContextProvider'
import { useGetRequest, useLoad } from '../../../shared/hooks/requests'
import {
    ALLOWED_REGIONS,
    DISTRICTS_LIST,
} from '../../../shared/utils/urls'

export function TenantAnalyticsFilter({redirectId}) {
    const [hasUsedRedirect, setHasUsedRedirect] = useState(false)
    const [hasInitialized, setHasInitialized] = useState(false)
    const { setDateFilter } = useFilter()
    const { setAnalyticsFilter } = useFilter()

    const [localSearch, setLocalSearch] = useState(() => {
        const stored = sessionStorage.getItem('tenantSearch')
        return redirectId || stored || ''
    })
    const [localStartDate, setLocalStartDate] = useState(() => {
        return sessionStorage.getItem('tenantStartDate') || moment().startOf('day').format('YYYY-MM-DD')
    })

    const [localEndDate, setLocalEndDate] = useState(() => {
        return sessionStorage.getItem('tenantEndDate') || moment().startOf('day').add(1, 'day').format('YYYY-MM-DD')
    })

    const [localRegion, setLocalRegion] = useState(() =>
        sessionStorage.getItem('tenantRegion') || ''
    )

    const [localDistrict, setLocalDistrict] = useState(() =>
        sessionStorage.getItem('tenantDistrict') || ''
    )

    const loadRegions = useLoad({ url: ALLOWED_REGIONS, params: { country_id: '1' } }, [])
    const regions = loadRegions.response || []

    const getDistricts = useGetRequest({ url: DISTRICTS_LIST, params: { regionId: localRegion } })
    const districts = getDistricts.response || []

    const regionOptions = regions.map((region) => ({ value: region.id, label: region.name }))
    const districtOptions = districts.map((district) => ({ value: district.id, label: district.name }))

    useEffect(() => {
        if (localRegion) {
            getDistricts.request()
        }
    }, [localRegion])

    useEffect(() => {
        if (redirectId && !hasUsedRedirect) {
            setLocalSearch(redirectId)
            sessionStorage.setItem('tenantSearch', redirectId)

            setAnalyticsFilter((prev) => ({
                ...prev,
                search: redirectId,
            }))
            setHasUsedRedirect(true)

            const url = new URL(window.location.href)
            url.searchParams.delete('redirectId')
            window.history.replaceState({}, '', url)
        }
    }, [redirectId, hasUsedRedirect])


    useEffect(() => {
        if (!hasInitialized && regionOptions.length > 0) {
            const regionFromStorage = sessionStorage.getItem('tenantRegion')
            if (regionFromStorage) {
                setLocalRegion(regionFromStorage)
            }

            const districtFromStorage = sessionStorage.getItem('tenantDistrict')
            if (districtFromStorage) {
                setLocalDistrict(districtFromStorage)
            }

            setHasInitialized(true)
        }
    }, [regionOptions, hasInitialized])

    useEffect(() => {
        if (localDistrict && districtOptions.length > 0) {
            const isDistrictStillValid = districtOptions.some((d) => String(d.value) === String(localDistrict))
            if (!isDistrictStillValid) {
                setLocalDistrict('')
                sessionStorage.removeItem('tenantDistrict')
            }
        }
    }, [districtOptions])

    return (
        <LocalizationProvider dateAdapter={AdapterMoment} locale="ru">
            <Paper square className="flex flex-col gap-2.5 p-3 overflow-x-auto">
                <h3 className="text-m text-black font-500">Filter</h3>
                <div className="flex items-center gap-2">
                    {/* Search Input */}
                    <input
                        type="text"
                        value={localSearch}
                        onChange={(e) => {
                            const val = e.target.value
                            setLocalSearch(val)
                            sessionStorage.setItem('tenantSearch', val)
                        }}
                        className="w-[12rem] outline-none h-[37px] border border-gray-200/50 rounded-[5px] px-[10px]"
                        placeholder="MTTID or username"
                    />

                    {/* Date Pickers */}
                    <div className="flex gap-2">
                        <div className="w-[150px]">
                            <DatePicker
                                label="Start Date"
                                sx={{
                                    height: 37,
                                    width: '150px',
                                    '& .MuiInputBase-root': { height: 37 },
                                    '& .MuiFormLabel-root': { top: '-8px' },
                                }}
                                inputFormat="DD-MM-YYYY"
                                value={moment(localStartDate)}
                                onChange={(e) => {
                                    const val = e ? moment(e).format('YYYY-MM-DD') : ''
                                    setLocalStartDate(val)
                                    sessionStorage.setItem('tenantStartDate', val)
                                    if (val && moment(val).isAfter(localEndDate)) {
                                        const newEnd = val
                                        setLocalEndDate(newEnd)
                                        sessionStorage.setItem('tenantEndDate', newEnd)
                                    }
                                }}
                            />
                        </div>

                        <div className="w-[150px]">
                            <DatePicker
                                label="End Date"
                                sx={{
                                    height: 37,
                                    width: '150px',
                                    '& .MuiInputBase-root': { height: 37 },
                                    '& .MuiFormLabel-root': { top: '-8px' },
                                }}
                                inputFormat="DD-MM-YYYY"
                                value={moment(localEndDate)}
                                minDate={moment(localStartDate)}
                                onChange={(e) => {
                                    const val = e ? moment(e).format('YYYY-MM-DD') : ''
                                    setLocalEndDate(val)
                                    sessionStorage.setItem('tenantEndDate', val)
                                }}
                            />
                        </div>
                    </div>

                    {/* Region Select */}
                    <Select
                        variant="outlined"
                        displayEmpty
                        renderValue={(value) => {
                            if (!value) return 'Choose region'
                            const selected = regionOptions.find((v) => String(v.value) === String(value))
                            return selected?.label || value
                        }}
                        sx={{ height: 37, width: '10rem' }}
                        value={localRegion}
                        onChange={(e) => {
                            const val = String(e.target.value)
                            setLocalRegion(val)
                            sessionStorage.setItem('tenantRegion', val)
                        }}
                    >
                        {!loadRegions.loading && regionOptions.length ? (
                            regionOptions.map((menuItem) => (
                                <MenuItem key={menuItem.value} value={menuItem.value}>
                                    {menuItem?.label}
                                </MenuItem>
                            ))
                        ) : loadRegions.loading ? (
                            <MenuItem>
                                <Skeleton width="50%" height={30} />
                            </MenuItem>
                        ) : (
                            <MenuItem value="">Nothing Found</MenuItem>
                        )}
                    </Select>

                    {/* District Select */}
                    <Select
                        variant="outlined"
                        displayEmpty
                        renderValue={(value) => {
                            if (!value) return 'Choose district'
                            const selected = districtOptions.find((v) => String(v.value) === String(value))
                            return selected?.label || 'Choose district'
                        }}
                        sx={{ height: 37, width: '10rem' }}
                        value={localDistrict}
                        disabled={!districtOptions.length}
                        onChange={(e) => {
                            const val = String(e.target.value)
                            setLocalDistrict(val)
                            sessionStorage.setItem('tenantDistrict', val)
                        }}
                    >
                        {!getDistricts.loading && districtOptions.length ? (
                            districtOptions.map((menuItem) => (
                                <MenuItem key={menuItem.value} value={menuItem.value}>
                                    {menuItem?.label}
                                </MenuItem>
                            ))
                        ) : getDistricts.loading ? (
                            <MenuItem>
                                <Skeleton width="50%" height={30} />
                            </MenuItem>
                        ) : (
                            <MenuItem value="">Nothing Found</MenuItem>
                        )}
                    </Select>

                    {/* Search Button */}
                    <div className="flex gap-2">
                        <button
                            className="px-[15px] py-[7px] bg-main-100 rounded-[6px] text-white font-[500]"
                            onClick={() => {
                                setDateFilter({
                                    startPeriod: localStartDate,
                                    endPeriod: localEndDate,
                                })
                                setAnalyticsFilter({
                                    region: localRegion,
                                    district: localDistrict,
                                    search: localSearch,
                                })
                            }}
                        >
                            Search
                        </button>

                        <button
                            className="px-[15px] py-[7px] bg-main-100 rounded-[6px] text-white font-[500]"
                            onClick={() => {
                                setLocalSearch('')
                                sessionStorage.removeItem('tenantSearch')

                                const today = moment().startOf('day').format('YYYY-MM-DD')
                                const tomorrow = moment().startOf('day').add(1, 'day').format('YYYY-MM-DD')

                                setLocalStartDate(today)
                                setLocalEndDate(tomorrow)
                                setLocalRegion('')
                                setLocalDistrict('')

                                sessionStorage.removeItem('tenantStartDate')
                                sessionStorage.removeItem('tenantEndDate')
                                sessionStorage.removeItem('tenantRegion')
                                sessionStorage.removeItem('tenantDistrict')

                                setDateFilter({
                                    startPeriod: today,
                                    endPeriod: tomorrow,
                                })

                                setAnalyticsFilter({
                                    region: '',
                                    district: '',
                                    search: '',
                                })
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </Paper>
        </LocalizationProvider>
    )
}
